import React, { useMemo, useState } from "react";

// Keep this aligned with backend asset-context extraction, which analyzes 4 assets.
const MAX_ASSETS = 4;
const formatAssetLabel = (url) => {
  try {
    const parsed = new URL(url);
    const pathname = parsed.pathname || "";
    const fromPath = pathname.split("/").filter(Boolean).pop();
    return fromPath || parsed.hostname || url;
  } catch (e) {
    return url;
  }
};

const AiInstructionAssetUploader = ({
  assetUrls = [],
  onChange,
  websiteId,
  environment,
  disabled = false,
  hidden = false,
}) => {
  if (hidden) return null;

  const [isUploading, setIsUploading] = useState(false);
  const safeAssetUrls = useMemo(
    () => Array.from(new Set((assetUrls || []).filter(Boolean))),
    [assetUrls]
  );

  const openPicker = () => {
    if (disabled || isUploading) return;
    if (safeAssetUrls.length >= MAX_ASSETS) return;
    if (typeof window.uploads?.select !== "function" || !websiteId) return;

    setIsUploading(true);
    window.uploads.select("callback", {
      environment,
      path: `/websites/${websiteId}/`,
      website_id: websiteId,
      library: false,
      callback: (fileUrl) => {
        setIsUploading(false);
        if (!fileUrl || typeof fileUrl !== "string") return;
        const normalized = fileUrl.trim();
        if (!normalized) return;
        const next = Array.from(new Set([...(safeAssetUrls || []), normalized]));
        onChange?.(next.slice(0, MAX_ASSETS));
      },
      onClose: () => setIsUploading(false),
    });
  };

  const removeAsset = (urlToRemove) => {
    if (disabled) return;
    onChange?.(safeAssetUrls.filter((url) => url !== urlToRemove));
  };

  return (
    <div style={{ marginBottom: "10px" }}>
      {safeAssetUrls.length > 0 && (
        <div className="relative z-[2] mb-2 flex flex-wrap gap-2 max-w-[240px]">
          {safeAssetUrls.map((url) => (
            <div
              key={url}
              className="relative inline-flex items-center gap-2 max-w-[220px] rounded-full border border-gray-300 bg-gray-100 text-gray-700 pl-3 pr-7 py-1 shadow-sm"
              title={url}
            >
              <span className="text-[11px] truncate max-w-[160px]">
                {formatAssetLabel(url)}
              </span>
              <button
                type="button"
                onClick={() => removeAsset(url)}
                className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-black/75 text-white flex items-center justify-center border-0 cursor-pointer hover:bg-black/85 shadow focus:outline-none"
                aria-label="Remove asset"
                title="Remove asset"
              >
                <span className="text-white text-[11px] leading-none font-semibold">
                  ×
                </span>
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center">
        <button
          type="button"
          onClick={openPicker}
          disabled={
            disabled || isUploading || safeAssetUrls.length >= MAX_ASSETS
          }
          className="w-[30px] h-[30px] rounded-full border-solid border-[1px] border-gray-300 bg-white text-gray-600 flex items-center justify-center shrink-0 hover:bg-gray-50 hover:border-gray-400 transition-colors p-0 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ flex: "0 0 30px", minWidth: 30, minHeight: 30 }}
          title="Attach asset"
          aria-label="Attach asset"
        >
          <span className="text-3xl leading-none font-light" style={{ marginTop: -2 }}>
            +
          </span>
        </button>
      </div>
    </div>
  );
};

export default AiInstructionAssetUploader;
